// frontend/src/components/shared/modals/SemesterManagementModal.tsx

import { useState, useEffect, useRef } from 'react';
import { Plus, AlertTriangle, Bookmark, X } from 'lucide-react';
import { ModalShell } from '../../ui';
import { useAuth } from '../../../hooks/useAuth';
import { useInstitutionConfig } from '../../../contexts/InstitutionConfigContext';
import { typedApi } from '../../../services/typedApiClient';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { extractSemestersToDelete } from '../../../utils/destructiveSemester';

interface AvailableSemester {
  id: string;
  name: string;
  is_selected?: boolean;
  course_count?: number;
  semester_id?: string | number;
}

interface ApiSemester {
  semester_code: string;
  semester: string;
  year: number;
  is_selected?: boolean;
  course_count?: number;
  semester_id?: string | number;
}

type ConfirmDialogType = 'ap' | 'other' | 'bookmarks';

interface ConfirmDialogState {
  type: ConfirmDialogType;
  count: number;
  credits: number;
}

export interface SemesterManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (result: unknown) => Promise<void> | void;
  planId: number | null;

  apCreditsCount?: number;
  apTotalCredits?: number;
  otherCreditsCount?: number;
  otherTotalCredits?: number;
  onOpenAPCredits?: () => void;
  onAddOtherCredit?: () => void;

  bookmarkedCoursesCount?: number;
  bookmarkedTotalCredits?: number;
  onOpenBookmarkedCourses?: () => void;
  onSetShowBookmarkedCourses?: (visible: boolean) => void;

  initialAPCreditsSelected?: boolean;
  initialOtherCreditsSelected?: boolean;
  initialBookmarkedCoursesVisible?: boolean;
}

const SemesterManagementModal = ({
  isOpen,
  onClose,
  onSave,
  planId,
  apCreditsCount = 0,
  apTotalCredits = 0,
  otherCreditsCount = 0,
  otherTotalCredits = 0,
  onOpenAPCredits,
  onAddOtherCredit,
  bookmarkedCoursesCount = 0,
  bookmarkedTotalCredits = 0,
  onOpenBookmarkedCourses,
  onSetShowBookmarkedCourses,
  initialAPCreditsSelected = false,
  initialOtherCreditsSelected = false,
  initialBookmarkedCoursesVisible = true,
}: SemesterManagementModalProps) => {
  // Workstream N7: gate the AP Credits row on the institution having
  // loaded AP policy data. Institutions without an authored AP YAML
  // (pre-N6) get the row hidden — there's nothing useful to manage.
  const { config: institutionConfig } = useInstitutionConfig();
  const showApCreditsRow = institutionConfig.supportsApCredits;

  const [availableSemesters, setAvailableSemesters] = useState<AvailableSemester[]>([]);
  const [selectedSemesters, setSelectedSemesters] = useState<string[]>([]);
  const [apCreditsSelected, setAPCreditsSelected] = useState(initialAPCreditsSelected);
  const [otherCreditsSelected, setOtherCreditsSelected] = useState(initialOtherCreditsSelected);
  const [bookmarkedCoursesVisible, setBookmarkedCoursesVisible] = useState(
    initialBookmarkedCoursesVisible
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

  // Escape closes the nested confirm first; ModalShell (blockDismiss) keeps
  // the outer modal open while the confirm is up, and handles plain Escape.
  useEscapeKey(() => {
    setConfirmDialog(null);
  }, isOpen && confirmDialog !== null);

  const { user } = useAuth();

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      setAPCreditsSelected(initialAPCreditsSelected);
      setOtherCreditsSelected(initialOtherCreditsSelected);
      setBookmarkedCoursesVisible(initialBookmarkedCoursesVisible);
      hasInitializedRef.current = true;
    } else if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, initialAPCreditsSelected, initialOtherCreditsSelected, initialBookmarkedCoursesVisible]);

  const fetchAvailableSemesters = async (): Promise<void> => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    if (!planId) {
      setError('No plan selected. Please create or select an academic plan first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await typedApi.academicPlans.getAvailableSemesters(planId);
      setAvailableSemesters((data.semesters || []) as AvailableSemester[]);
    } catch (err) {
      console.error('Error fetching semesters:', err);
      setError((err as { message?: string })?.message ?? 'Failed to load semesters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !user?.id) return;

    const fetchSemesters = async (): Promise<void> => {
      if (!planId) {
        setError('Missing plan configuration');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await typedApi.academicPlans.getAvailableSemesters(planId);

        const semesters: AvailableSemester[] = [];
        if (data.semesters_by_year) {
          (Object.values(data.semesters_by_year).flat() as ApiSemester[]).forEach((sem) => {
            semesters.push({
              id: sem.semester_code,
              name: `${sem.semester.charAt(0).toUpperCase() + sem.semester.slice(1)} ${sem.year}`,
              is_selected: sem.is_selected,
              course_count: sem.course_count || 0,
              semester_id: sem.semester_id,
            });
          });
        }

        const initialSelected = semesters.filter((s) => s.is_selected).map((s) => s.id);
        setSelectedSemesters(initialSelected);
        setAvailableSemesters(semesters);
      } catch (err) {
        console.error('Error fetching semesters:', err);
        setError((err as { message?: string })?.message ?? 'Failed to load semesters');
      } finally {
        setLoading(false);
      }
    };

    fetchSemesters();
  }, [isOpen, user?.id, planId]);

  const handleSemesterToggle = (semesterId: string): void => {
    setSelectedSemesters((prev) =>
      prev.includes(semesterId) ? prev.filter((id) => id !== semesterId) : [...prev, semesterId]
    );
  };

  const handleAPCreditsChange = (checked: boolean): void => {
    if (!checked && apCreditsCount > 0) {
      setConfirmDialog({ type: 'ap', count: apCreditsCount, credits: apTotalCredits });
    } else {
      setAPCreditsSelected(checked);
    }
  };

  const handleOtherCreditsChange = (checked: boolean): void => {
    if (!checked && otherCreditsCount > 0) {
      setConfirmDialog({ type: 'other', count: otherCreditsCount, credits: otherTotalCredits });
    } else {
      setOtherCreditsSelected(checked);
    }
  };

  const handleBookmarkedCoursesChange = (checked: boolean): void => {
    if (!checked && bookmarkedCoursesCount > 0) {
      setConfirmDialog({
        type: 'bookmarks',
        count: bookmarkedCoursesCount,
        credits: bookmarkedTotalCredits,
      });
    } else {
      setBookmarkedCoursesVisible(checked);
    }
  };

  const handleConfirmDelete = (): void => {
    if (confirmDialog?.type === 'ap') {
      setAPCreditsSelected(false);
    } else if (confirmDialog?.type === 'other') {
      setOtherCreditsSelected(false);
    } else if (confirmDialog?.type === 'bookmarks') {
      setBookmarkedCoursesVisible(false);
    }
    setConfirmDialog(null);
  };

  const handleSave = async (): Promise<void> => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    if (!planId) {
      setError('No plan selected. Please create or select an academic plan first.');
      return;
    }

    try {
      const allSelectedSemesters = [...selectedSemesters];
      if (apCreditsSelected) {
        allSelectedSemesters.push('000008');
      }
      if (otherCreditsSelected) {
        allSelectedSemesters.push('000009');
      }

      let result;
      try {
        result = await typedApi.academicPlans.updateSemesters(planId, allSelectedSemesters);
      } catch (err) {
        // RC5-1 guard: the unchecked selection would delete populated semesters.
        // The list here comes from explicit checkboxes (authoritative intent),
        // and unchecking AP/Other/Bookmark buckets is already gated by the
        // confirm dialogs above, so this explicit Save is the confirmation —
        // retry with confirm_destructive=true. The guard still protects
        // programmatic callers whose keep-list derives from stale plan state.
        if (extractSemestersToDelete(err) === null) throw err;
        result = await typedApi.academicPlans.updateSemesters(
          planId, allSelectedSemesters, true,
        );
      }

      if (onSetShowBookmarkedCourses) {
        onSetShowBookmarkedCourses(bookmarkedCoursesVisible);
      }

      await onSave(result);
      onClose();
    } catch (err) {
      console.error('Error saving semesters:', err);
      setError((err as { message?: string })?.message ?? 'Failed to save semesters');
    }
  };

  const handleRetry = (): void => {
    fetchAvailableSemesters();
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      ariaLabel="Manage semesters"
      blockDismiss={confirmDialog !== null}
      maxWidth="max-w-md"
      className="p-6"
    >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Manage Semesters</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-control hover:bg-surface-sunken transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 mb-4">
          Select which semesters to include in your academic plan
        </p>

        {error && (
          <div className="mb-4 p-3 bg-status-critical/10 border border-status-critical/30 text-status-critical-fg rounded-card">
            {error}
            <button onClick={handleRetry} className="ml-2 underline hover:no-underline">
              Try Again
            </button>
          </div>
        )}

        <div className="mb-4 space-y-2">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Prior Learning Credits
          </div>

          {showApCreditsRow && (
            <label
              data-testid="ap-credits-row"
              className="flex items-center justify-between p-3 border rounded-card bg-red-400/10 border-line-soft cursor-pointer hover:bg-red-400/15 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={apCreditsSelected}
                  onChange={(e) => handleAPCreditsChange(e.target.checked)}
                  className="rounded border-line-soft text-primary focus:ring-primary"
                />
                <span className="w-2.5 h-2.5 rounded-full bg-red-400 flex-shrink-0" aria-hidden="true" />
                <span className="font-semibold text-ink">AP Credits</span>
              </div>
              <div className="flex items-center space-x-3">
                {apCreditsCount > 0 ? (
                  <span className="bg-red-400/15 text-status-critical-fg px-3 py-1 rounded-full text-xs font-medium">
                    {apCreditsCount} course{apCreditsCount !== 1 ? 's' : ''} • {apTotalCredits} cr
                  </span>
                ) : (
                  <span className="text-gray-500 text-xs">No credits</span>
                )}
                {onOpenAPCredits && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenAPCredits();
                      onClose();
                    }}
                    className="p-1.5 rounded-control hover:bg-red-400/20 transition-colors"
                    title="Add AP Credits"
                    aria-label="Add AP Credits"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                  </button>
                )}
              </div>
            </label>
          )}

          <label className="flex items-center justify-between p-3 border rounded-card bg-indigo-400/10 border-line-soft cursor-pointer hover:bg-indigo-400/15 transition-colors">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={otherCreditsSelected}
                onChange={(e) => handleOtherCreditsChange(e.target.checked)}
                className="rounded border-line-soft text-primary focus:ring-primary"
              />
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-400 flex-shrink-0" aria-hidden="true" />
              <span className="font-semibold text-ink">Other Credits</span>
            </div>
            <div className="flex items-center space-x-3">
              {otherCreditsCount > 0 ? (
                <span className="bg-indigo-400/15 text-status-progress-fg px-3 py-1 rounded-full text-xs font-medium">
                  {otherCreditsCount} course{otherCreditsCount !== 1 ? 's' : ''} •{' '}
                  {otherTotalCredits} cr
                </span>
              ) : (
                <span className="text-gray-500 text-xs">No credits</span>
              )}
              {onAddOtherCredit && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAddOtherCredit();
                    onClose();
                  }}
                  className="p-1.5 rounded-control hover:bg-indigo-400/20 transition-colors"
                  title="Add Other Credit"
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                </button>
              )}
            </div>
          </label>

          <label className="flex items-center justify-between p-3 border-2 rounded-lg bg-stone-100 border-stone-200 cursor-pointer hover:bg-stone-150 transition-colors">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={bookmarkedCoursesVisible}
                onChange={(e) => handleBookmarkedCoursesChange(e.target.checked)}
                className="rounded border-gray-300 text-stone-600 focus:ring-stone-500"
              />
              <span className="font-semibold text-gray-800">Bookmarked Courses</span>
            </div>
            <div className="flex items-center space-x-3">
              {bookmarkedCoursesCount > 0 ? (
                <span className="bg-stone-600 text-white px-3 py-1 rounded-full text-xs font-medium shadow-sm">
                  {bookmarkedCoursesCount} course{bookmarkedCoursesCount !== 1 ? 's' : ''} •{' '}
                  {bookmarkedTotalCredits} cr
                </span>
              ) : (
                <span className="text-gray-500 text-xs">No bookmarks</span>
              )}
              {onOpenBookmarkedCourses && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenBookmarkedCourses();
                    onClose();
                  }}
                  className="p-1.5 rounded hover:bg-stone-200 transition-colors"
                  title="View Bookmarked Courses"
                >
                  <Bookmark className="w-4 h-4 text-gray-600" />
                </button>
              )}
            </div>
          </label>
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          Academic Semesters
        </div>

        {loading ? (
          <div className="text-center py-4">Loading semesters...</div>
        ) : availableSemesters.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No semesters available</div>
        ) : (
          <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
            {availableSemesters.map((semester) => {
              // Tier 3.5c: same season grammar as the Academic Plan —
              // identity dot + low-alpha wash (mode-safe), one hue map.
              const getSeasonStyles = (semesterName: string): { wash: string; dot: string } => {
                const season = semesterName.toLowerCase();
                if (season.includes('winter'))
                  return { wash: 'bg-blue-400/10 hover:bg-blue-400/15', dot: 'bg-blue-400' };
                if (season.includes('spring'))
                  return { wash: 'bg-green-500/10 hover:bg-green-500/15', dot: 'bg-green-500' };
                if (season.includes('summer'))
                  return { wash: 'bg-yellow-400/10 hover:bg-yellow-400/15', dot: 'bg-yellow-400' };
                if (season.includes('fall'))
                  return { wash: 'bg-orange-400/10 hover:bg-orange-400/15', dot: 'bg-orange-400' };
                return { wash: 'bg-surface-sunken/70 hover:bg-surface-sunken', dot: 'bg-gray-400' };
              };
              const seasonStyles = getSeasonStyles(semester.name);

              return (
                <label
                  key={semester.id}
                  className={`flex items-center justify-between space-x-2 p-3 border border-line-soft rounded-card cursor-pointer transition-colors ${seasonStyles.wash}`}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedSemesters.includes(semester.id)}
                      onChange={() => handleSemesterToggle(semester.id)}
                      className="rounded border-line-soft text-primary focus:ring-primary"
                    />
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${seasonStyles.dot}`} aria-hidden="true" />
                    <span className="font-semibold text-ink">{semester.name}</span>
                  </div>
                  <div className="text-sm">
                    {semester.course_count && semester.course_count > 0 ? (
                      <span className="bg-surface-sunken text-ink-2 px-3 py-1 rounded-full text-xs font-medium">
                        {semester.course_count} course{semester.course_count !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-ink-3 text-xs">No courses</span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !user?.id}
            className="px-4 py-2 bg-primary text-white rounded-control hover:bg-primary-800 disabled:opacity-50"
          >
            Save Changes
          </button>
        </div>

        {confirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-status-critical/15 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete{' '}
                    {confirmDialog.type === 'ap'
                      ? 'AP Credits'
                      : confirmDialog.type === 'other'
                        ? 'Other Credits'
                        : 'Bookmarked Courses'}
                    ?
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    This will permanently delete {confirmDialog.count} course
                    {confirmDialog.count !== 1 ? 's' : ''} ({confirmDialog.credits} credits) from
                    your plan.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-status-critical-strong text-white rounded-control hover:bg-status-critical-strong/90"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
    </ModalShell>
  );
};

export default SemesterManagementModal;
